import { Fragment, type ReactNode } from 'react';

import { JalaliDate, Money, Num, Percent } from '@bookora/ui';

import type { GuideTextValueResponse } from '@/api/generated/model';

/**
 * A tip's text: a Persian template with `{token}` holes, and typed values.
 *
 * The server never bakes a number into a sentence. «۱٬۲۴۰ بیمار» written as
 * bare text inside a right-to-left paragraph is laid out by the bidi algorithm,
 * and a grouped figure next to a neutral paints its groups backwards; a date
 * «۱۴۰۵/۰۸/۲۵» next to a space does the same. So each value is rendered by the
 * kit primitive for its kind, each an isolate with the right base direction:
 *
 *   COUNT        → `<Num>`                     grouped, Persian digits
 *   MONEY_RIALS  → `<Money rials>`             rials in, toman out
 *   DATE         → `<JalaliDate variant="long">` «۲۵ آبان ۱۴۰۵», no hover
 *   PERCENT_BP   → `<Percent value={bp / 100}>` basis points → a percentage
 *   TEXT         → `<bdi>`                     a name the clinic typed
 *
 * A token with no value renders nothing (and says so in development): a hole in
 * the sentence is better than «{count}» on a clinic owner's dashboard.
 */

const TOKEN = /\{(\w+)\}/g;

function renderValue(value: GuideTextValueResponse): ReactNode {
  switch (value.kind) {
    case 'COUNT':
      return <Num value={value.number ?? null} />;
    case 'MONEY_RIALS':
      return <Money rials={value.number ?? null} />;
    case 'DATE':
      return <JalaliDate value={value.date ?? null} variant="long" withRelative={false} />;
    case 'PERCENT_BP':
      return (
        <Percent
          value={value.number === null || value.number === undefined ? null : value.number / 100}
          decimals={0}
        />
      );
    case 'TEXT':
      return value.text ? <bdi>{value.text}</bdi> : null;
    default:
      return null;
  }
}

/** The template as React nodes; text runs stay text, every value is its primitive. */
export function renderGuideText(
  template: string,
  values: readonly GuideTextValueResponse[],
): ReactNode[] {
  const byToken = new Map(values.map((value) => [value.token, value]));
  const out: ReactNode[] = [];
  let last = 0;
  let index = 0;
  for (const match of template.matchAll(TOKEN)) {
    const start = match.index ?? 0;
    if (start > last) out.push(template.slice(last, start));
    const token = match[1] ?? '';
    const value = byToken.get(token);
    if (value) {
      out.push(<Fragment key={`v${index}`}>{renderValue(value)}</Fragment>);
    } else if (import.meta.env.DEV) {
      console.warn(`[guide] no value for token «${token}» in «${template}»`);
    }
    index += 1;
    last = start + match[0].length;
  }
  if (last < template.length) out.push(template.slice(last));
  return out;
}
